import { EmailRecipient, EmailRecipientsConfig, RecipientVariable } from '@common/types';
import { isEmpty, uniqBy } from 'lodash';
import { useMemo } from 'react';
import { UserRole } from 'enums';
import { useSearchUser } from 'hooks';
import { PartialSubmission, User, UserDetails } from 'types';
import {
  convertAssigneeToRecipientGroup,
  convertSubmissionContactsToRecipientGroup,
  convertUserToRecipient,
  createRecipientGroupFromSubmissionCustomer,
  RecipientGroup,
} from 'broker/components/Emails/recipient-utils';

function buildRecipientsList(
  baseUser: UserDetails | undefined,
  additionalUserIds: string[],
  userMap: Map<string, User>,
) {
  const baseRecipients = baseUser ? [convertUserToRecipient(baseUser)] : [];
  const additionalUsers = additionalUserIds.map((id) => userMap.get(id)).filter(Boolean) as User[];
  const additionalRecipients = additionalUsers.map((user) => convertUserToRecipient(user));
  return [...baseRecipients, ...additionalRecipients];
}

function getToRecipientGroup(
  toConfig: EmailRecipient[] | undefined,
  submission: PartialSubmission,
  userMap: Map<string, User>,
): RecipientGroup[] {
  if (!toConfig || isEmpty(toConfig)) {
    return [];
  }

  // Separate recipient variables (Insured, Client, Assignee) from user IDs (additional IDs)
  const recipientVariables = toConfig.filter((item) =>
    Object.values(RecipientVariable).includes(item as RecipientVariable),
  ) as RecipientVariable[];
  const userIds = toConfig.filter((item) => !Object.values(RecipientVariable).includes(item as RecipientVariable));

  if (isEmpty(recipientVariables)) {
    const users = userIds.map((id) => userMap.get(id)).filter(Boolean) as User[];
    if (!isEmpty(users)) {
      const recipients = users.map((user) => convertUserToRecipient(user));
      // Assume all users have the same role, use the first user's role as the type
      const usersRole = users[0].role;
      // If users are Flow brokers, create a recipient group from submission assignee with these users
      if (usersRole === UserRole.CapitolaBroker) {
        return [convertAssigneeToRecipientGroup(submission, recipients)];
      }
      // Otherwise, create a recipient group from submission customer with these users
      return [createRecipientGroupFromSubmissionCustomer(submission, recipients)];
    }
    return [];
  }

  // Use first recipient variable as the main type
  const recipientType = recipientVariables[0];

  if (recipientType === RecipientVariable.Insured) {
    // For insured, just use submission contacts, ignore additional IDs
    return [convertSubmissionContactsToRecipientGroup(submission)];
  }

  if (recipientType === RecipientVariable.Client) {
    const recipients = buildRecipientsList(submission.user, userIds, userMap);
    return [createRecipientGroupFromSubmissionCustomer(submission, isEmpty(recipients) ? undefined : recipients)];
  }

  if (recipientType === RecipientVariable.Assignee) {
    const recipients = buildRecipientsList(submission.assignee, userIds, userMap);
    return [convertAssigneeToRecipientGroup(submission, isEmpty(recipients) ? undefined : recipients)];
  }

  return [];
}

/**
 * Process CC/BCC recipients configuration into email addresses
 * Supports recipient variables (Client, FlowBroker) and user IDs
 */
function getCcRecipients(
  recipientsConfig: EmailRecipient[] | undefined,
  submission: PartialSubmission,
  userMap: Map<string, User>,
): string[] {
  if (!recipientsConfig) {
    return [];
  }

  const emails: string[] = [];

  recipientsConfig.forEach((recipient) => {
    if (recipient === RecipientVariable.Insured) {
      // Skip, currently we don't support insured for CC/BCC
      return;
    }

    if (recipient === RecipientVariable.Assignee) {
      if (submission.assignee?.email) {
        emails.push(submission.assignee.email);
      }
      return;
    }

    if (recipient === RecipientVariable.Client) {
      if (submission.user?.email) {
        emails.push(submission.user.email);
      }
      return;
    }

    // It's a user ID
    const user = userMap.get(recipient);
    if (user?.email) {
      emails.push(user.email);
    }
  });

  return emails;
}

/**
 * Hook to get pre-selected email recipients and initial CC/BCC emails for placement task emails,
 * based on email recipients configuration.
 *
 * Handles both recipient variables (insured, client) and additional IDs (user IDs).
 *
 * @param submission - The submission to build recipients from (can be null/undefined)
 * @param emailRecipientsConfig - The email recipients configuration (to, cc, bcc)
 */
export function useGetPlacementTaskEmailRecipients(
  submission: PartialSubmission | null | undefined,
  emailRecipientsConfig?: EmailRecipientsConfig,
): {
  preSelectedRecipients: RecipientGroup[];
  ccEmailsInit: string[];
  bccEmailsInit: string[];
} {
  // Fetch users from submission team (client team)
  const { items: submissionTeamUsers = [] } = useSearchUser({
    filter: {
      teamId: submission?.team?.id,
      enabled: true,
    },
    enabled: !!submission?.team?.id,
  });

  // Fetch users from assignee team (Flow team)
  const { items: assigneeTeamUsers = [] } = useSearchUser({
    filter: {
      teamId: submission?.assignee?.teamId,
      enabled: true,
    },
    enabled: !!submission?.assignee?.teamId,
  });

  const userMap = useMemo(() => {
    const map = new Map<string, User>();
    uniqBy([...submissionTeamUsers, ...assigneeTeamUsers], 'id').forEach((user) => map.set(user.id, user));
    return map;
  }, [assigneeTeamUsers, submissionTeamUsers]);

  // Return empty arrays if no submission
  if (!submission) {
    return {
      preSelectedRecipients: [],
      ccEmailsInit: [],
      bccEmailsInit: [],
    };
  }

  const toRecipients = getToRecipientGroup(emailRecipientsConfig?.to, submission, userMap);
  const ccEmails = getCcRecipients(emailRecipientsConfig?.cc, submission, userMap);
  const bccEmails = getCcRecipients(emailRecipientsConfig?.bcc, submission, userMap);

  return {
    preSelectedRecipients: toRecipients,
    ccEmailsInit: ccEmails,
    bccEmailsInit: bccEmails,
  };
}
